import { AsyncPipe } from "@angular/common";
import { Component, computed, effect, inject, input, output } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { MonitoringProductImageService } from "@domain/product/monitoring/monitoring-product-image.service";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanCustomPartNumbersComponent } from "@project/floorplanner/floorplan-custom-part-numbers/floorplan-custom-part-numbers.component";
import { FloorplanItemAttachmentsButtonComponent } from "@project/floorplanner/floorplan-item-attachments-button/floorplan-item-attachments-button.component";
import { FloorplanItemGroupComponent } from "@project/floorplanner/floorplan-item-group/floorplan-item-group.component";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import {
  buildPatientMonitorGroupedSourceItems,
  PatientMonitorGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getMonitoringConfigurationSummaryTitle,
  getPatientMonitorGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { shareReplay, switchMap } from "rxjs";

@Component({
  selector: "app-patient-monitor-notes",
  imports: [
    FloorplanItemDetailsComponent,
    FloorplanItemGroupComponent,
    FloorplanItemAttachmentsButtonComponent,
    FloorplanCustomPartNumbersComponent,
    AsyncPipe,
  ],
  templateUrl: "./patient-monitor-notes.component.html",
  styleUrl: "./patient-monitor-notes.component.scss",
  standalone: true,
})
export class PatientMonitorNotesComponent {
  private readonly monitoringProductImageService = inject(MonitoringProductImageService);

  readonly floorplanItem = input.required<FloorplanItem>();
  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly toggleExtendedButtonClicked = output<void>();
  protected readonly patientMonitor = computed(() => this.floorplanItem() as FloorplanPatientMonitor);
  protected readonly MonitoringCategory = MonitoringCategory;
  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  protected productItems!: MonitoringProductItem[];
  protected monitoringItems: MonitoringGroupedPartSourceItem[] = [];
  protected readonly fallbackImageUrl = "./assets/floorplan-menu-icons/patient_monitor_menu.svg";
  protected monitoringTitle = getMonitoringConfigurationSummaryTitle(MonitoringCategory.PATIENT_MONITORS);
  private readonly categoryLabels: PatientMonitorGroupedPartLabels = getPatientMonitorGroupedPartLabels();
  protected readonly imageUrl$ = toObservable(this.patientMonitor).pipe(
    switchMap((patientMonitor) => this.monitoringProductImageService.resolvePatientMonitorImageUrl(patientMonitor.config)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    effect(() => {
      const patientMonitor = this.patientMonitor();
      this.productItems = patientMonitor.config.getOptions();
      this.monitoringItems = this.buildMonitoringItems(patientMonitor.config);
    });
  }

  private buildMonitoringItems(config: PatientMonitorConfiguration): MonitoringGroupedPartSourceItem[] {
    return buildPatientMonitorGroupedSourceItems(
      {
        family: config.family,
        sensor: config.sensor,
        mountingGroup: config.mountingGroup,
        mountingArm: config.mountingArm,
        attachments: config.attachments,
      },
      this.categoryLabels,
    );
  }
}
